// ============================================================
// content.js — single source of truth for all site copy.
// Edit this file to change site content; components read from it.
// ============================================================

export const meta = {
  name: "David Ndung'u",
  shortName: "David Iguta",
  title: "David Ndung'u — AI Engineer",
  description:
    "AI Engineer building multi-agent LLM systems and the distributed infrastructure that makes them trustworthy — durable execution, human-in-the-loop checkpoints, and deterministic guardrails.",
  email: "davidiguta@gmail.com",
  linkedin: "https://www.linkedin.com/in/david-ndung-u-183382127/",
  github: "https://github.com/Iguta",
  headshot:
    "https://portfolio-website-iguta.s3.us-east-1.amazonaws.com/DSC_6327.JPG",
};

export const hero = {
  availability:
    "Open to: AI Engineering • Agentic AI • ML Engineering • AI Infrastructure",
  greeting: "Hi, I'm",
  name: "David Ndung'u",
  headlineTail: "I build AI agent systems that survive contact with production.",
  subhead:
    "AI Engineer working on multi-agent LLM systems and the distributed infrastructure underneath them — durable execution, human-in-the-loop checkpoints, and deterministic guardrails that bound what a model is allowed to ship.",
  // Set `href` on the write-up once it is published to make the button appear.
  ctas: [
    { label: "View Work", href: "#projects", variant: "primary" },
    {
      label: "Read: How I think about agent durability",
      href: null,
      variant: "ghost",
    },
    { label: "Contact", href: "#contact", variant: "ghost" },
  ],
};

export const about = {
  heading: "About Me",
  paragraphs: [
    "I'm an AI Engineer at Innov8.ai, where I build multi-agent systems for education — and, more to the point, the infrastructure that keeps them correct when they run for hours across real cloud failures. The pedagogy comes from faculty; my job is turning their objectives into a system that can actually enforce them.",
    "Most of my work lives at the intersection of LLM orchestration and distributed systems: specialist agents coordinated in LangGraph, durable execution checkpointed to DynamoDB, an SQS-driven worker fleet on ECS, and content-addressed versioning that can prove exactly what a revision changed and what it left untouched. The interesting problems aren't in the prompting. They're in what happens when a worker dies mid-run, when a human takes three days to approve something, or when a model tries to edit a file nobody asked it to touch.",
    "My approach comes out of nearly three years as an SDET on enterprise financial platforms: models propose, validators dispose. Anywhere an agent has write power, a deterministic runtime check bounds it — not a prompt. Systems should fail closed and loudly rather than quietly guessing.",
    "I hold an M.S. in Applied Data Science from Indiana University Indianapolis and a B.S. in Electrical & Electronics Engineering from the University of Nairobi.",
  ],
};

export const experience = [
  {
    role: "AI Engineer",
    company: "Innov8.ai",
    location: "Indianapolis, IN",
    dates: "Jan 2026 – Present",
    bullets: [
      "Translated faculty-defined pedagogical requirements into technical specifications and system architecture, then built an agentic course-creation platform (unreleased) in which seven specialist agents take an instructor from course concept to a complete publishable university course, published directly to Canvas LMS — compressing authoring from months to hours.",
      "Work directly with faculty subject-matter experts to convert high-level learning objectives into agent responsibilities, validation rules, and typed artifact contracts the system can enforce.",
      "Architected durable execution on LangGraph with DynamoDB checkpointing as the single source of execution truth: agent work decomposes into resumable turns dispatched over SQS to an ECS Fargate worker fleet, so runs park at human approval checkpoints for days and resume losslessly across worker restarts and fleet deploys.",
      "Built a content-addressed course ledger on S3 with sha256-addressed blobs, atomic release commits, and fork-based revision — proving byte-for-byte what a revision changed and what carried forward untouched.",
      "Designed a propose-confirm-enforce pattern for instructor revisions: prose feedback compiles to a typed scope proposal, the instructor confirms it, and the confirmed scope is bound to content hashes and machine-enforced by a fail-closed diff validator.",
      "Built Agora, implementing a faculty-originated Socratic teaching model as a production system now running in live K–12 classroom instruction — RAG grounding in primary source texts, persona architecture, content safety for a minor-facing product, and a structured evaluation framework gating releases.",
    ],
  },
  {
    role: "Graduate Research Assistant",
    company: "COMET Lab, IU Indianapolis",
    location: "Indianapolis, IN",
    dates: "Sep 2025 – Dec 2025",
    bullets: [
      "Designed and evaluated agentic AI systems for faculty and student learning experiences.",
      "Prototyped multi-step decision-making workflows using LLM agent frameworks.",
      "Assessed model quality and reliability against structured evaluation criteria.",
    ],
  },
  {
    role: "Software Development Engineer in Test",
    company: "Safaricom PLC",
    location: "Nairobi, Kenya",
    dates: "Feb 2022 – Aug 2024",
    bullets: [
      "Designed and executed 500+ functional and regression test cases for enterprise financial platforms; automated 200+ regression tests, cutting testing cycle time ~40%.",
      "Built a security validation framework alongside a team of 10 quality engineers, embedding OWASP Top 10 checks, SAST, and dependency scanning into automated test pipelines.",
      "Integrated automated testing into Jenkins/Git CI/CD pipelines; spearheaded blue-green deployment testing for zero-downtime releases.",
    ],
  },
  {
    role: "Teaching Assistant, Math & Logical Foundations",
    company: "IU Indianapolis",
    location: "Indianapolis, IN",
    dates: "Jan 2025 – Apr 2025",
    bullets: [
      "Supported 30+ graduate students in mathematical reasoning and logic for data science through weekly 1-on-1 sessions and detailed feedback.",
    ],
  },
];

export const projects = [
  {
    title: "Agentic course creation for higher education",
    tag: "unreleased",
    blurb:
      "Built from faculty-defined pedagogical requirements: seven specialist agents (instructional design, authoring, editorial and ethics review, resource curation) coordinated in LangGraph, running on an SQS-driven ECS worker fleet with DynamoDB-checkpointed durable execution and content-addressed versioning on S3. Instructors stay the author of record: every consequential step passes a typed human checkpoint, and deterministic validators — not prompts — bound what agents can write.",
    stack: [
      "LangGraph",
      "Python",
      "AWS ECS",
      "SQS",
      "DynamoDB",
      "S3",
      "Pydantic",
      "Canvas LMS",
    ],
    repo: null,
    demo: null,
    icon: "brain",
    viz: { type: "bar", data: [3, 5, 8, 7, 11, 14, 12] },
  },
  {
    title: "Agora — Socratic AI dialogue for classrooms",
    blurb:
      "A faculty-originated Socratic teaching model implemented as a production system: conversational AI personas of historical and intellectual figures, running in live K–12 classroom instruction. RAG grounding in curated primary sources keeps thinkers faithful to the record; a persona layer enforces Socratic behavior (agents question rather than answer); a safety layer handles the requirements of a minor-facing product.",
    stack: ["Next.js", "D-ID", "Clerk", "Sanity", "RAG", "LLM evaluation"],
    repo: null,
    // Gated behind sign-in, so it is labelled as a visit rather than a demo.
    demo: "https://agora.aegentisai.com/sign-in",
    demoLabel: "Visit Agora",
    icon: "line",
    viz: { type: "waveform", data: [0, 8, 14, -6, 18, -4, 12, 2] },
  },
  {
    title: "AutoSDLC — Multi-agent software delivery",
    blurb:
      "An end-to-end pipeline where Business Analyst, Developer, QA, and DevOps agents collaborate across requirements, implementation, testing, and deployment with human-in-the-loop controls. Agents orchestrated over MCP servers; checkpointing and resumable execution let the pipeline recover from failure mid-run. Shipped a fully functional output application, not a demo.",
    stack: ["LangGraph", "OpenAI SDK", "MCP", "Playwright"],
    repo: null,
    demo: null,
    icon: "bar",
    viz: {
      type: "heatmap",
      data: [
        0.9, 0.6, 0.7, 0.8, 0.5, 0.7, 0.85, 0.6, 0.8, 0.9, 0.95, 0.7, 0.6, 0.8,
        0.85, 0.9,
      ],
    },
  },
];

export const earlierProjects = [
  {
    title: "Student Graduation Risk Prediction",
    blurb:
      "XGBoost on SageMaker (88% accuracy, 79% recall), Streamlit frontend.",
    repo: "https://github.com/Iguta/student-graduation-prediction-ssagemaker-inference",
    demo: "https://students-graduation-prediction.streamlit.app/",
  },
  {
    title: "Clinical Trials Analytics Pipeline",
    blurb:
      "S3/Glue/Spark/Athena ETL over 470K+ records, QuickSight dashboards.",
    repo: null,
    demo: null,
  },
  {
    title: "Food Vision",
    blurb:
      "EfficientNetV2 transfer learning, 101-class classification, Hugging Face Spaces.",
    repo: "https://huggingface.co/spaces/davidiguta/food-vision-app",
    demo: "https://huggingface.co/spaces/davidiguta/food-vision-app",
  },
  {
    title: "Speech Emotion Recognition",
    blurb: "Spectrogram features and neural networks.",
    repo: "https://colab.research.google.com/drive/16ORj7psFC7gihBOo_I_98nD97gI1iLSc",
    demo: null,
  },
];

export const skills = [
  {
    title: "LLM & Agent Systems",
    icon: "brain",
    items: [
      "LangGraph",
      "LangChain",
      "OpenAI SDK",
      "MCP",
      "Multi-agent orchestration",
      "RAG",
      "Prompt & persona design",
      "LLM evaluation",
      "Guardrails",
    ],
  },
  {
    title: "Distributed Systems & Cloud",
    icon: "cloud",
    items: [
      "AWS ECS Fargate",
      "SQS",
      "DynamoDB",
      "S3",
      "Lambda",
      "Durable execution",
      "Event-driven workers",
      "Content-addressed storage",
      "Docker",
    ],
  },
  {
    title: "Languages & Core",
    icon: "code",
    items: ["Python", "SQL", "TypeScript/JavaScript", "Pydantic"],
  },
  {
    title: "Machine Learning",
    icon: "line",
    items: [
      "PyTorch",
      "scikit-learn",
      "XGBoost",
      "Deep learning",
      "Transfer learning",
      "SageMaker",
    ],
  },
  {
    title: "Reliability & Testing",
    icon: "database",
    items: [
      "PyTest",
      "Fault-matrix & property-based testing",
      "Playwright",
      "CI/CD",
      "Fail-closed design",
    ],
  },
  {
    title: "Application",
    icon: "library",
    items: ["Next.js", "React", "FastAPI", "Streamlit", "Canvas LMS"],
  },
];

export const education = [
  {
    school: "Indiana University Indianapolis",
    degree: "Master of Science in Applied Data Science",
    dates: "Sep 2024 – Dec 2025",
    detail:
      "Relevant Coursework: Machine Learning, Deep Learning, Data Mining, Cloud Computing, Data Analytics, Visualization.",
  },
  {
    school: "University of Nairobi",
    degree: "Bachelor of Science in Electrical and Electronics Engineering",
    dates: "May 2016 – Sep 2021",
    detail: null,
  },
];

export const agentSection = {
  heading: "Chat with My AI Agent",
  blurb:
    "Explore my work, my approach to agent systems, and my background through an interactive assistant — a faster way to know me.",
  cta: "Ask me Anything",
};

export const contact = {
  heading: "Contact",
  blurb: "Let's build something impactful together.",
};
